import {useSyntheticChange} from '@github-ui/use-synthetic-change'
import {useCallback, useEffect, useRef, useState} from 'react'

// Hook for handling markdown input
export function useInputManagement(onChange: (value: string) => void) {
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [inputKey, setInputKey] = useState(1)

  // Keep the latest `onChange` in a ref so the handlers below stay referentially stable across
  // renders. Consumers commonly pass a fresh `onChange` identity on every keystroke (e.g. an
  // inline arrow function). If `onInputChange`/`emitChange` changed with it, that instability would
  // propagate through `useFileHandling` into the `MarkdownEditorContext`/`SavedRepliesContext`
  // values and force the whole toolbar to re-render on every keystroke.
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  })

  const onInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChangeRef.current(e.target.value)
  }, [])

  const reset = useCallback(() => {
    onChangeRef.current('')
    setInputKey(key => key + 1)
  }, [])

  const emitChange = useSyntheticChange({inputRef, fallbackEventHandler: onInputChange})

  return {
    inputRef,
    inputKey,
    onInputChange,
    reset,
    emitChange,
  }
}
